/**
 * @voltxampere/ui — Math channel expression engine (Sprint 15)
 *
 * Per ADR-0024. M1 / M2 channels carry user expressions like
 * `ch1 + ch2`, `Math.sqrt(ch1**2 + ch2**2)`, `Math.sin(t * 2 *
 * Math.PI * 1000)`. We tokenise + AST-walk + evaluate; the
 * `eval()` global is forbidden (XSS-grade risk inside a shared
 * studio surface). The whitelist of identifiers + functions makes
 * the surface auditable — anything not on the list is a parse
 * error, not a silent identity coercion.
 *
 * Variable names available inside expressions:
 *   - `ch1`, `ch2`, `ch3`, `ch4` — probe channel sample values
 *   - `m1`, `m2` — math channel sample values (M2 may reference M1)
 *   - `t`              — sample timestamp (seconds)
 *   - `Math.PI`, `Math.E` — physical constants
 *
 * Functions on the whitelist:
 *   - `Math.sin`, `Math.cos`, `Math.tan`, `Math.asin`, `Math.acos`,
 *     `Math.atan`, `Math.atan2`
 *   - `Math.sqrt`, `Math.abs`, `Math.log`, `Math.log10`,
 *     `Math.log2`, `Math.exp`, `Math.pow`
 *   - `Math.min`, `Math.max`, `Math.floor`, `Math.ceil`,
 *     `Math.round`
 *
 * Operators: `+`, `-`, `*`, `/`, `**`, unary `-`, parens.
 *
 * Cycle detection (`detectCycle`): M2 may reference M1 but M1
 * cannot reference M2 — that is a DAG order requirement enforced
 * at parse time.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
import { isMathChannel } from "./types.js";
const ALLOWED_FUNCTIONS = new Set([
    "Math.sin",
    "Math.cos",
    "Math.tan",
    "Math.asin",
    "Math.acos",
    "Math.atan",
    "Math.atan2",
    "Math.sqrt",
    "Math.abs",
    "Math.log",
    "Math.log10",
    "Math.log2",
    "Math.exp",
    "Math.pow",
    "Math.min",
    "Math.max",
    "Math.floor",
    "Math.ceil",
    "Math.round",
]);
const ALLOWED_VARIABLES = new Set(["ch1", "ch2", "ch3", "ch4", "m1", "m2", "t"]);
const ALLOWED_CONSTANTS = {
    "Math.PI": Math.PI,
    "Math.E": Math.E,
};
function tokenize(source) {
    const tokens = [];
    let i = 0;
    while (i < source.length) {
        const ch = source[i] ?? "";
        // Whitespace.
        if (/\s/.test(ch)) {
            i++;
            continue;
        }
        // Numbers (decimal, scientific).
        if (/[0-9]/.test(ch) || (ch === "." && /[0-9]/.test(source[i + 1] ?? ""))) {
            let j = i;
            while (j < source.length && /[0-9.]/.test(source[j] ?? ""))
                j++;
            // Optional exponent.
            if (j < source.length && (source[j] === "e" || source[j] === "E")) {
                j++;
                if (j < source.length && (source[j] === "+" || source[j] === "-"))
                    j++;
                while (j < source.length && /[0-9]/.test(source[j] ?? ""))
                    j++;
            }
            const slice = source.slice(i, j);
            const value = Number(slice);
            if (!Number.isFinite(value)) {
                throw new Error(`Invalid number literal "${slice}" at position ${String(i)}`);
            }
            tokens.push({ kind: "number", value: slice, position: i });
            i = j;
            continue;
        }
        // Identifiers (ch1, Math.sin, t, etc.).
        if (/[A-Za-z_]/.test(ch)) {
            let j = i;
            while (j < source.length && /[A-Za-z0-9_.]/.test(source[j] ?? ""))
                j++;
            tokens.push({ kind: "ident", value: source.slice(i, j), position: i });
            i = j;
            continue;
        }
        // Operators.
        if (ch === "*" && source[i + 1] === "*") {
            tokens.push({ kind: "op", value: "**", position: i });
            i += 2;
            continue;
        }
        if ("+-*/".includes(ch)) {
            tokens.push({ kind: "op", value: ch, position: i });
            i++;
            continue;
        }
        if (ch === "(") {
            tokens.push({ kind: "lparen", value: ch, position: i });
            i++;
            continue;
        }
        if (ch === ")") {
            tokens.push({ kind: "rparen", value: ch, position: i });
            i++;
            continue;
        }
        if (ch === ",") {
            tokens.push({ kind: "comma", value: ch, position: i });
            i++;
            continue;
        }
        throw new Error(`Unexpected character "${ch}" at position ${String(i)}`);
    }
    return tokens;
}
// ── Recursive-descent parser ───────────────────────────────────────
class Parser {
    tokens;
    pos = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    parse() {
        const expr = this.parseAdditive();
        if (this.pos < this.tokens.length) {
            const t = this.tokens[this.pos];
            throw new Error(`Unexpected token "${t?.value ?? ""}" at position ${String(t?.position ?? -1)}`);
        }
        return expr;
    }
    parseAdditive() {
        let left = this.parseMultiplicative();
        while (this.match("op", "+") || this.match("op", "-")) {
            const op = (this.tokens[this.pos - 1]?.value ?? "+");
            const right = this.parseMultiplicative();
            left = { kind: "binary", op, left, right };
        }
        return left;
    }
    parseMultiplicative() {
        let left = this.parsePower();
        while (this.match("op", "*") || this.match("op", "/")) {
            const op = (this.tokens[this.pos - 1]?.value ?? "*");
            const right = this.parsePower();
            left = { kind: "binary", op, left, right };
        }
        return left;
    }
    parsePower() {
        const left = this.parseUnary();
        if (this.match("op", "**")) {
            // Right-associative.
            const right = this.parsePower();
            return { kind: "binary", op: "**", left, right };
        }
        return left;
    }
    parseUnary() {
        if (this.match("op", "-")) {
            return { kind: "unary", op: "-", operand: this.parseUnary() };
        }
        if (this.match("op", "+")) {
            return { kind: "unary", op: "+", operand: this.parseUnary() };
        }
        return this.parsePrimary();
    }
    parsePrimary() {
        const tok = this.tokens[this.pos];
        if (!tok)
            throw new Error("Unexpected end of expression");
        if (tok.kind === "number") {
            this.pos++;
            return { kind: "literal", value: Number(tok.value) };
        }
        if (tok.kind === "lparen") {
            this.pos++;
            const expr = this.parseAdditive();
            if (!this.match("rparen", ")")) {
                throw new Error(`Expected ')' at position ${String(tok.position)}`);
            }
            return expr;
        }
        if (tok.kind === "ident") {
            this.pos++;
            // Function call?
            if (this.match("lparen", "(")) {
                const args = [];
                if (!this.peek("rparen", ")")) {
                    args.push(this.parseAdditive());
                    while (this.match("comma", ",")) {
                        args.push(this.parseAdditive());
                    }
                }
                if (!this.match("rparen", ")")) {
                    throw new Error(`Expected ')' after arguments at position ${String(tok.position)}`);
                }
                return { kind: "call", name: tok.value, args };
            }
            // Constant or variable.
            return { kind: "variable", name: tok.value };
        }
        throw new Error(`Unexpected token "${tok.value}" at position ${String(tok.position)}`);
    }
    match(kind, value) {
        const t = this.tokens[this.pos];
        if (t?.kind !== kind || t.value !== value)
            return false;
        this.pos++;
        return true;
    }
    peek(kind, value) {
        const t = this.tokens[this.pos];
        return t?.kind === kind && t.value === value;
    }
}
export function parseMathExpression(source) {
    try {
        const ast = new Parser(tokenize(source)).parse();
        validateAst(ast);
        const references = collectChannelReferences(ast);
        return { source, ast, references, error: null };
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return { source, ast: null, references: [], error: message };
    }
}
function validateAst(node) {
    switch (node.kind) {
        case "literal":
            return;
        case "variable":
            if (!ALLOWED_VARIABLES.has(node.name) &&
                !Object.prototype.hasOwnProperty.call(ALLOWED_CONSTANTS, node.name)) {
                throw new Error(`Unknown identifier "${node.name}"`);
            }
            return;
        case "unary":
            validateAst(node.operand);
            return;
        case "binary":
            validateAst(node.left);
            validateAst(node.right);
            return;
        case "call":
            if (!ALLOWED_FUNCTIONS.has(node.name)) {
                throw new Error(`Function "${node.name}" is not on the math whitelist`);
            }
            for (const a of node.args)
                validateAst(a);
            return;
    }
}
function collectChannelReferences(node) {
    const out = new Set();
    walk(node, (n) => {
        if (n.kind === "variable") {
            const name = n.name;
            if (name === "ch1" ||
                name === "ch2" ||
                name === "ch3" ||
                name === "ch4" ||
                name === "m1" ||
                name === "m2") {
                out.add(name);
            }
        }
    });
    return [...out];
}
function walk(node, visit) {
    visit(node);
    switch (node.kind) {
        case "unary":
            walk(node.operand, visit);
            return;
        case "binary":
            walk(node.left, visit);
            walk(node.right, visit);
            return;
        case "call":
            for (const a of node.args)
                walk(a, visit);
            return;
        case "literal":
        case "variable":
            return;
    }
}
function callFunction(name, args) {
    switch (name) {
        case "Math.sin":
            return Math.sin(args[0] ?? 0);
        case "Math.cos":
            return Math.cos(args[0] ?? 0);
        case "Math.tan":
            return Math.tan(args[0] ?? 0);
        case "Math.asin":
            return Math.asin(args[0] ?? 0);
        case "Math.acos":
            return Math.acos(args[0] ?? 0);
        case "Math.atan":
            return Math.atan(args[0] ?? 0);
        case "Math.atan2":
            return Math.atan2(args[0] ?? 0, args[1] ?? 0);
        case "Math.sqrt":
            return Math.sqrt(args[0] ?? 0);
        case "Math.abs":
            return Math.abs(args[0] ?? 0);
        case "Math.log":
            return Math.log(args[0] ?? 0);
        case "Math.log10":
            return Math.log10(args[0] ?? 0);
        case "Math.log2":
            return Math.log2(args[0] ?? 0);
        case "Math.exp":
            return Math.exp(args[0] ?? 0);
        case "Math.pow":
            return Math.pow(args[0] ?? 0, args[1] ?? 0);
        case "Math.min":
            return Math.min(...args);
        case "Math.max":
            return Math.max(...args);
        case "Math.floor":
            return Math.floor(args[0] ?? 0);
        case "Math.ceil":
            return Math.ceil(args[0] ?? 0);
        case "Math.round":
            return Math.round(args[0] ?? 0);
        default:
            throw new Error(`Unknown function ${name}`);
    }
}
function evalAst(node, env) {
    switch (node.kind) {
        case "literal":
            return node.value;
        case "variable": {
            if (Object.prototype.hasOwnProperty.call(ALLOWED_CONSTANTS, node.name)) {
                return ALLOWED_CONSTANTS[node.name] ?? 0;
            }
            const v = env[node.name];
            if (v === undefined) {
                throw new Error(`Variable "${node.name}" not defined for this sample`);
            }
            return v;
        }
        case "unary": {
            const v = evalAst(node.operand, env);
            return node.op === "-" ? -v : v;
        }
        case "binary": {
            const l = evalAst(node.left, env);
            const r = evalAst(node.right, env);
            switch (node.op) {
                case "+":
                    return l + r;
                case "-":
                    return l - r;
                case "*":
                    return l * r;
                case "/":
                    return l / r;
                case "**":
                    return l ** r;
            }
            // unreachable — exhaustive switch above.
            throw new Error("unreachable binary op");
        }
        case "call": {
            const args = node.args.map((a) => evalAst(a, env));
            return callFunction(node.name, args);
        }
    }
}
/**
 * Evaluate a math expression sample-by-sample. Returns a new
 * Float64Array sized to the smallest of the referenced channels'
 * sample arrays. Returns `null` when the expression failed to
 * parse or any required channel buffer is missing.
 */
export function evaluateMath(expr, samples) {
    if (!expr.ast)
        return null;
    // Determine the output length — the shortest of the referenced
    // channel buffers. If a referenced channel has no samples the
    // result is a zero-length buffer (consumer treats as 'no data').
    let length = Number.POSITIVE_INFINITY;
    for (const ref of expr.references) {
        const buf = samples[ref];
        if (!buf)
            return null;
        if (buf.length < length)
            length = buf.length;
    }
    if (!Number.isFinite(length)) {
        // Expression has no channel references — single-sample length
        // anchored on the time array if available, else 0.
        length = samples.time?.length ?? 0;
    }
    const out = new Float64Array(length);
    const time = samples.time ?? new Float64Array(0);
    for (let i = 0; i < length; i++) {
        const env = { t: time[i] ?? 0 };
        for (const ref of expr.references) {
            env[ref] = samples[ref]?.[i] ?? 0;
        }
        try {
            out[i] = evalAst(expr.ast, env);
        }
        catch {
            out[i] = Number.NaN;
        }
    }
    return out;
}
/**
 * Detect a forbidden cycle between math channels. Sprint 15
 * permits M2 to reference M1 (DAG forward) but forbids M1 from
 * referencing M2 (cycle). Returns true when the supplied pair
 * forms a cycle.
 */
export function detectCycle(m1Expr, m2Expr) {
    const m1RefsM2 = m1Expr.references.includes("m2");
    const m2RefsM1 = m2Expr.references.includes("m1");
    // M1 → M2 forms a cycle if M2 also references M1, OR the M1
    // expression itself is "referencing the future". Sprint 15
    // policy: M1 may NEVER reference M2 (M1 is the upstream slot).
    if (m1RefsM2)
        return true;
    // Self-reference is also a cycle.
    if (m1Expr.references.includes("m1"))
        return true;
    if (m2Expr.references.includes("m2"))
        return true;
    // M2 referencing M1 is allowed; the cycle only forms if M1 also
    // refs M2 (handled above), so the m2RefsM1 path alone is OK.
    void m2RefsM1;
    return false;
}
/** True when the given channel id refers to a math slot. */
export function isMathSlot(id) {
    return isMathChannel(id);
}
//# sourceMappingURL=math-evaluator.js.map